import { Link, useLocation } from 'react-router-dom';
import {
  Menu,
  X,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { cn } from '../lib/utils';
import { useState, useEffect, FC } from 'react';
import { Button } from './ui/button';
import { UserNav } from './UserNav';
import { useAuth } from '../hooks/useAuth';
import OnboardingProgress from './vendor/onboarding/OnboardingProgress';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';

// Define the expected structure for navigation items
interface NavItem {
  name: string;
  href?: string;
  icon: React.ElementType;
  children?: NavItem[];
}

// Define the expected structure for navigation groups
interface NavGroup {
  title?: string;
  items: NavItem[];
}

// Define props for the AppSidebar component
interface AppSidebarProps {
  navigation: NavGroup[];
}

const AppSidebar: FC<AppSidebarProps> = ({ navigation }) => {
  const location = useLocation();
  const { profile, user } = useAuth(); // Added user to destructuring
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});

  // Check if current user is a vendor
  const isVendor = profile?.role === 'Vendor';

  // Auto-expand SEO if we're on an SEO-related page
  useEffect(() => {
    if (location.pathname.startsWith('/cupshup/seo') || location.pathname.includes('/blog-writing')) {
      setExpandedItems(prev => ({ ...prev, 'SEO': true }));
    }
  }, [location.pathname]);

  const toggleExpanded = (itemName: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemName]: !prev[itemName]
    }));
  };

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Close menu on escape key press
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const NavigationList = () => (
    <ul role="list" className="flex flex-1 flex-col gap-y-5 px-4">
      {navigation.map((group, groupIndex) => (
        <li key={groupIndex}>
          {group.title && (
            <h3 className="mb-2 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
              {group.title}
            </h3>
          )}
          <ul role="list" className="-mx-2 space-y-1">
            {group.items.map((item) => {
              // Check if this item or any of its children is active
              const isActive = item.href
                ? location.pathname.startsWith(item.href)
                : item.children?.some((child: NavItem) => child.href && location.pathname.startsWith(child.href));

              // If item has children, render as expandable
              if (item.children) {
                return (
                  <li key={item.name}>
                    <button
                      onClick={() => toggleExpanded(item.name)}
                      className={cn(
                        'w-full group flex items-center justify-between gap-x-3 rounded-md p-2 text-sm leading-6',
                        isActive
                          ? 'text-white'
                          : 'text-white/80 hover:text-white hover:bg-white/10'
                      )}
                    >
                      <div className="flex items-center gap-x-3">
                        <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                        {item.name}
                      </div>
                      {expandedItems[item.name] ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                    {expandedItems[item.name] && (
                      <ul className="mt-1 space-y-1">
                        {item.children.map((child: NavItem) => {
                          const isChildActive = child.href && location.pathname.startsWith(child.href);
                          return (
                            <li key={child.name}>
                              <Link
                                to={child.href!}
                                className={cn(
                                  isChildActive
                                    ? 'bg-white/10 text-white'
                                    : 'text-white/70 hover:text-white hover:bg-white/10',
                                  'group flex gap-x-3 rounded-md p-2 pl-11 text-sm leading-6'
                                )}
                              >
                                {child.name}
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              }

              // Regular item without children
              return (
                <li key={item.name}>
                  <Link
                    to={item.href!}
                    className={cn(
                      'group flex gap-x-3 rounded-md p-2 text-sm leading-6',
                      isActive
                        ? 'bg-white/10 text-white'
                        : 'text-white/80 hover:text-white hover:bg-white/10'
                    )}
                  >
                    <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                    {item.name}
                  </Link>
                </li>
              );
            })}
          </ul>
        </li>
      ))}
    </ul>
  );

  const UserProfile = () => {
    const displayName = profile?.name || user?.user_metadata?.full_name || 'User Profile';
    const initials = displayName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);

    // Determine path based on role
    let profilePath = '/';
    if (profile?.role === 'Client') profilePath = '/client/profile';
    else if (profile?.role === 'Vendor') profilePath = '/vendor/profile';
    else if (profile?.role === 'CupShup') profilePath = '/cupshup/profile';

    return (
      <Link
        to={profilePath}
        className="border-t border-white/5 p-4 hover:bg-white/5 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9 border border-white/10">
            <AvatarImage src={profile?.profile_photo || user?.user_metadata?.avatar_url} alt={displayName} />
            <AvatarFallback className="bg-primary/20 text-primary text-xs font-bold">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate">{displayName}</p>
            <p className="text-[10px] text-gray-400 truncate">View Profile</p>
          </div>
        </div>
      </Link>
    );
  };

  const logoUrl = "/CupShupLogo.png";

  // Dashboard path based on role
  let dashboardPath = '/';
  if (profile?.role === 'Client') dashboardPath = '/client/dashboard';
  else if (profile?.role === 'Vendor') dashboardPath = '/vendor';
  else if (profile?.role === 'CupShup') dashboardPath = '/cupshup';

  return (
    <>
      {/* Mobile Header */}
      <div className="lg:hidden sticky top-0 z-50 flex h-14 items-center justify-between gap-4 border-b border-gray-200 bg-white px-4 shadow-sm">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className="h-5 w-5 text-gray-700" />
            ) : (
              <Menu className="h-5 w-5 text-gray-700" />
            )}
          </Button>
          <Link to={dashboardPath}>
            <div className="relative h-8 w-8 flex items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-primary/20 to-blue-500/20 p-1">
              <img
                src={logoUrl}
                alt="CupShup"
                className="h-full w-full object-contain drop-shadow-[0_0_8px_rgba(0,169,121,0.3)]"
              />
            </div>
          </Link>
        </div>
        <div className="flex items-center gap-2">
          <UserNav />
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setIsMobileMenuOpen(false)}>
          <div
            className="fixed inset-y-0 left-0 w-72 bg-[#1d2121] shadow-xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex h-16 items-center gap-3 border-b border-white/5 px-4">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded flex items-center justify-center overflow-hidden">
                  <img src={logoUrl} alt="CupShup" className="h-full w-full object-contain" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">
                    CupShup
                  </h1>
                </div>
              </div>
            </div>
            {/* Show onboarding progress for vendor users in mobile menu */}
            {isVendor && (
              <div className="px-3 pt-4 pb-2">
                <OnboardingProgress showInSidebar={true} />
              </div>
            )}
            <NavigationList />
            <UserProfile />
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-[#1d2121] border-r border-white/5">
          <div className="flex h-16 shrink-0 items-center gap-3 px-6 border-b border-white/5">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded flex items-center justify-center overflow-hidden">
                <img src={logoUrl} alt="CupShup" className="h-full w-full object-contain" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">
                  CupShup
                </h1>
              </div>
            </div>
          </div>
          {/* Show onboarding progress for vendor users in desktop sidebar */}
          {isVendor && (
            <div className="px-3 -mt-2">
              <OnboardingProgress showInSidebar={true} />
            </div>
          )}
          <NavigationList />
          <UserProfile />
        </div>
      </div>
    </>
  );
}

export default AppSidebar;